import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import DateInput from "../../components/DateInput";
import { Label } from "../../components/ui/label";
import { ToggleSwitch as Switch } from "../../components/ui/toggle-switch";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../../components/ui/dialog";
import {
  ArrowLeft, Building2, Package, MapPin, Check, X, Plus, Save, Tag, Clock, Sparkles,
  Bot, AlertTriangle, Bell, Phone, Pencil, ChevronRight, Shield, Lock, Eye,
  RefreshCw, Euro, Receipt, CreditCard, FileText, Users, Trash2, UserCog, HardHat,
  Link2, CheckCircle2
} from "lucide-react";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const TIPO_LABELS = { base: "BASE", addon: "ADD-ON", servizio: "SERVIZIO" };

const Section = ({ title, icon: Icon, iconColor, children, badge, actions }) => (
  <div className="bg-white rounded-2xl border border-gray-200 shadow-sm overflow-hidden" data-testid={`section-${title.toLowerCase().replace(/\s/g, "-")}`}>
    <div className="px-5 py-4 border-b border-gray-100 flex items-center justify-between">
      <div className="flex items-center gap-3">
        <div className={`w-8 h-8 rounded-xl flex items-center justify-center ${iconColor || "bg-gray-100"}`}>
          <Icon className="h-4 w-4 text-white" />
        </div>
        <h2 className="text-sm font-bold text-gray-900 uppercase tracking-wide">{title}</h2>
        {badge}
      </div>
      {actions}
    </div>
    <div className="p-5">{children}</div>
  </div>
);

const InfoRow = ({ label, value, highlight }) => (
  <div className="flex items-center justify-between py-2 border-b border-gray-50 last:border-0">
    <span className="text-xs text-gray-500">{label}</span>
    <span className={`text-sm font-semibold ${highlight || "text-gray-800"}`}>{value}</span>
  </div>
);

const SuperAdminSocietaDetail = ({ token, societaId, onBack, packages: allPackages }) => {
  const [impersonating, setImpersonating] = useState(null); // "admin" | "operatore" | null
  const [loading, setLoading] = useState(true);
  const [societa, setSocieta] = useState(null);
  const [details, setDetails] = useState(null);
  const [subscription, setSubscription] = useState(null);
  const [tgConfig, setTgConfig] = useState(null);
  const [calcServer, setCalcServer] = useState(null);
  const headers = { Authorization: `Bearer ${token}` };

  // Edit states
  const [editingSection, setEditingSection] = useState(null);
  const [editForm, setEditForm] = useState({});
  const [initialEditForm, setInitialEditForm] = useState({});
  const [confirmDialog, setConfirmDialog] = useState(false);

  // Offer dialog
  const [offerDialog, setOfferDialog] = useState(false);
  const [offerItems, setOfferItems] = useState({ packages: {}, telegram_numeri: 0, locale_extra: 0 });
  const [offerPrezzo, setOfferPrezzo] = useState(0);
  const [offerNote, setOfferNote] = useState("");

  // Delete dialog
  const [deleteDialog, setDeleteDialog] = useState(false);
  const [deleteInput, setDeleteInput] = useState("");
  const [deleting, setDeleting] = useState(false);

  // Wizard activation
  const [activatingWizard, setActivatingWizard] = useState(false);
  const handleToggleWizard = async () => {
    setActivatingWizard(true);
    try {
      const r = await axios.put(`${API}/superadmin/societies/${societaId}/toggle-wizard`, {}, { headers });
      toast.success(r.data.action === "disabled"
        ? "Wizard DISATTIVATO (tutti gli step segnati come completati)"
        : "Wizard ATTIVATO (riapparirà al prossimo accesso)");
      fetchAll();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Errore toggle wizard");
    } finally { setActivatingWizard(false); }
  };

  // Venditori list
  const [venditori, setVenditori] = useState([]);

  const fetchAll = useCallback(async () => {
    try {
      const [socRes, detRes, vendRes] = await Promise.all([
        axios.get(`${API}/superadmin/societies`, { headers }),
        axios.get(`${API}/superadmin/societa/${societaId}/details`, { headers }),
        axios.get(`${API}/superadmin/venditori`, { headers }).catch(() => ({ data: [] })),
      ]);
      const soc = socRes.data.find(s => s.id === societaId);
      setSocieta(soc);
      setDetails(detRes.data);
      setSubscription(soc?.subscription || {});
      setVenditori(vendRes.data || []);
      try {
        const tgRes = await axios.get(`${API}/superadmin/telegram-config/${societaId}`, { headers });
        setTgConfig(tgRes.data);
      } catch { setTgConfig(null); }
    } catch { toast.error("Errore caricamento dati"); }
    finally { setLoading(false); }
  }, [societaId, token]);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  // ── Dirty tracking ──
  const isFieldDirty = (field) => {
    const curr = editForm[field];
    const init = initialEditForm[field];
    if (Array.isArray(curr) && Array.isArray(init)) return JSON.stringify([...curr].sort()) !== JSON.stringify([...init].sort());
    return curr !== init;
  };
  const dirtyBorder = (field) => isFieldDirty(field) ? "ring-2 ring-red-400 border-red-400" : "";
  const hasDirtyFields = Object.keys(editForm).some(k => isFieldDirty(k));
  const dirtyFields = Object.keys(editForm).filter(k => isFieldDirty(k));

  // ── Open edit for a section ──
  const startEdit = (section, data) => {
    setEditingSection(section);
    setEditForm({ ...data });
    setInitialEditForm(JSON.parse(JSON.stringify(data)));
  };
  const cancelEdit = () => { setEditingSection(null); setEditForm({}); setInitialEditForm({}); };

  // ── Save with confirm ──
  const saveEdit = async () => {
    if (hasDirtyFields && !confirmDialog) { setConfirmDialog(true); return; }
    setConfirmDialog(false);
    try {
      if (editingSection === "abbonamento") {
        const { numeri_acquistati, venditore_id, ...subData } = editForm;
        subData.addons = { telegram_bot: { numeri_acquistati } };
        subData.venditore_id = venditore_id || "";
        await axios.put(`${API}/superadmin/subscriptions/${societaId}`, subData, { headers });
      } else if (editingSection === "bot") {
        await axios.put(`${API}/superadmin/telegram-config/${societaId}`, {
          bot_token: editForm.bot_token, webhook_url: editForm.bot_webhook_url, attivo: editForm.bot_attivo,
        }, { headers });
        if (isFieldDirty("numeri_acquistati")) {
          await axios.put(`${API}/superadmin/subscriptions/${societaId}`, {
            addons: { telegram_bot: { numeri_acquistati: editForm.numeri_acquistati } },
          }, { headers });
        }
        if (isFieldDirty("destinatari")) {
          await axios.put(`${API}/superadmin/societa/${societaId}/destinatari`, { destinatari: editForm.destinatari }, { headers });
        }
      } else if (editingSection === "locali") {
        for (const locale of details.locali || []) {
          const wasDisabled = (initialEditForm.locali_disattivati || []).includes(locale.id);
          const isNowDisabled = (editForm.locali_disattivati || []).includes(locale.id);
          if (wasDisabled !== isNowDisabled) {
            await axios.put(`${API}/superadmin/societa/${societaId}/locale/${locale.id}/toggle`, {}, { headers });
          }
        }
      }
      toast.success("Salvato con successo");
      cancelEdit();
      fetchAll();
    } catch { toast.error("Errore nel salvataggio"); }
  };

  // ── Resolve request ──
  const resolveRequest = async (requestId, action) => {
    try {
      await axios.put(`${API}/superadmin/pending-requests/${requestId}/resolve`, { action }, { headers });
      toast.success(action === "approve" ? "Richiesta approvata!" : "Richiesta rifiutata.");
      fetchAll();
    } catch { toast.error("Errore"); }
  };

  // ── Offer logic ──
  const calcOfferTotal = () => {
    let total = 0;
    Object.keys(offerItems.packages).forEach(pkgId => {
      if (offerItems.packages[pkgId]) {
        const pkg = allPackages.find(p => p.id === pkgId);
        if (pkg) total += pkg.prezzo_mensile_annuale || 0;
      }
    });
    if (offerItems.telegram_numeri > 0) {
      const cfg = details?.addon_config?.telegram_bot || {};
      total += (cfg.prezzo_primo_numero || 10) + Math.max(0, offerItems.telegram_numeri - 1) * (cfg.prezzo_numero_extra || 5);
    }
    if (offerItems.locale_extra > 0) {
      const cfg = details?.addon_config?.locale_aggiuntivo || {};
      total += offerItems.locale_extra * (cfg.prezzo_extra_annuale || 20);
    }
    return total;
  };
  const offerOrigTotal = calcOfferTotal();
  const hasOfferItems = Object.values(offerItems.packages).some(v => v) || offerItems.telegram_numeri > 0 || offerItems.locale_extra > 0;

  const sendOffer = async () => {
    if (!hasOfferItems) { toast.error("Seleziona almeno un elemento"); return; }
    const items = [];
    Object.keys(offerItems.packages).forEach(pkgId => {
      if (offerItems.packages[pkgId]) {
        const pkg = allPackages.find(p => p.id === pkgId);
        const pr = pkg?.prezzo_mensile_annuale || 0;
        items.push({ tipo: "package", package_id: pkgId, quantita: 1, prezzo_unitario: pr });
      }
    });
    if (offerItems.telegram_numeri > 0) {
      const cfg = details?.addon_config?.telegram_bot || {};
      const tot = (cfg.prezzo_primo_numero || 10) + Math.max(0, offerItems.telegram_numeri - 1) * (cfg.prezzo_numero_extra || 5);
      items.push({ tipo: "telegram_bot", quantita: offerItems.telegram_numeri, prezzo_unitario: tot });
    }
    if (offerItems.locale_extra > 0) {
      const cfg = details?.addon_config?.locale_aggiuntivo || {};
      const pLoc = cfg.prezzo_extra_annuale || 20;
      items.push({ tipo: "locale_aggiuntivo", quantita: offerItems.locale_extra, prezzo_unitario: pLoc });
    }
    try {
      await axios.post(`${API}/superadmin/promo-offer`, { societa_id: societaId, items, prezzo_totale_originale: offerOrigTotal, prezzo_offerta: offerPrezzo, note: offerNote }, { headers });
      toast.success(offerPrezzo === 0 ? "Regalo inviato!" : "Offerta inviata!");
      setOfferDialog(false);
      fetchAll();
    } catch { toast.error("Errore"); }
  };

  // ── Cost calculation: chiamata al backend per sincronizzare il calcolo
  //    (deve stare PRIMA degli early return per non violare Rules of Hooks) ──
  useEffect(() => {
    if (!subscription) return;
    const sub = subscription || {};
    const addons = details?.addons || {};
    const addonCfg = details?.addon_config || {};
    const pkg_ids = (sub.package_ids || []);
    const tg_qty = (addons.telegram_bot?.numeri_acquistati) || sub.addon_telegram_qty || 0;
    const locInclusi = addonCfg.locale_aggiuntivo?.locali_inclusi || 1;
    const loc_extra = Math.max(0, (details?.locali_count || 0) - locInclusi);
    if (!pkg_ids.length && !tg_qty && !loc_extra) { setCalcServer({ subtotal: 0, addon_items: [] }); return; }
    axios.post(`${API}/superadmin/calculate-cart`,
      { package_ids: pkg_ids, addon_telegram_qty: tg_qty, addon_locale_extra: loc_extra },
      { headers }
    ).then(r => setCalcServer(r.data)).catch(() => setCalcServer(null));
  }, [subscription, details]); // eslint-disable-line

  if (loading) return <div className="flex items-center justify-center py-20"><div className="text-gray-400 animate-pulse text-sm">Caricamento...</div></div>;
  if (!societa) return <div className="text-center py-20 text-gray-400">Societa non trovata</div>;

  const deleteSocieta = async () => {
    if (deleteInput.trim().toLowerCase() !== "elimina") return;
    setDeleting(true);
    try {
      const res = await axios.delete(`${API}/superadmin/societies/${societaId}`, { headers });
      toast.success(res.data?.message || "Società eliminata");
      setDeleteDialog(false);
      onBack();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Errore durante l'eliminazione");
    } finally {
      setDeleting(false);
    }
  };

  const sub = subscription || {};
  const addons = details?.addons || {};
  const numAcq = addons.telegram_bot?.numeri_acquistati || 0;
  const destinatari = details?.destinatari || [];
  const ownedPkgs = (sub.package_ids || []).map(id => allPackages.find(p => p.id === id)).filter(Boolean);
  const wizardActive = details?.wizard_active ?? false;
  const pendingReqs = details?.pending_requests || [];
  const promoOffers = details?.promo_offers || [];
  const transactions = details?.transactions || [];
  const addonCfg = details?.addon_config || {};

  const calcCosts = () => {
    const lines = [];
    ownedPkgs.forEach(pkg => {
      const price = pkg.prezzo_mensile_annuale || 0;
      lines.push({ label: pkg.nome, tipo: TIPO_LABELS[pkg.tipo] || pkg.tipo, price });
    });
    if (numAcq > 0) {
      const tgCfg = addonCfg.telegram_bot || {};
      const p1 = tgCfg.prezzo_primo_numero ?? tgCfg.prezzo_mensile ?? 10;
      const pN = tgCfg.prezzo_numero_extra ?? 5;
      const tgTotal = p1 + Math.max(0, numAcq - 1) * pN;
      lines.push({ label: `Bot Telegram (${numAcq} num.)`, tipo: "ADD-ON", price: tgTotal });
    }
    const locCount = details?.locali_count || 0;
    const locInclusi = addonCfg.locale_aggiuntivo?.locali_inclusi || 1;
    const locExtra = Math.max(0, locCount - locInclusi);
    if (locExtra > 0) {
      const pLoc = addonCfg.locale_aggiuntivo?.prezzo_extra_annuale || 20;
      const locTotal = locExtra * pLoc;
      lines.push({ label: `${locExtra} locale${locExtra > 1 ? "i" : ""} extra`, tipo: "ADD-ON", price: locTotal });
    }
    // Usa il subtotal autorevole dal backend (include sconto combo)
    const total = calcServer?.subtotal != null
      ? calcServer.subtotal
      : lines.reduce((s, l) => s + (l.price || 0), 0);
    const comboLabel = calcServer?.combo_applied || null;
    const risparmio = calcServer?.risparmio || 0;
    return { lines, total, comboLabel, risparmio };
  };
  const costs = calcCosts();

  const labelMap = { package_ids: "Pacchetti", durata: "Durata", gratuito: "Gratuito", trial_giorni: "Trial giorni", trial_inizio: "Inizio trial", codice_sconto: "Codice sconto", numeri_acquistati: "Numeri Bot", bot_token: "Bot Token", bot_webhook_url: "Webhook", bot_attivo: "Bot attivo", destinatari: "Destinatari", locali_disattivati: "Locali" };

  /**
   * SuperAdmin "Entra come Admin/Operatore" — apre una NUOVA TAB con il token
   * impersonation passato via query param. La nuova tab usa sessionStorage
   * (scoped per tab) così la sessione SuperAdmin originale rimane intatta.
   */
  const handleImpersonate = async (role) => {
    if (impersonating) return;
    setImpersonating(role);
    try {
      const res = await axios.post(
        `${API}/superadmin/impersonate/${societaId}`,
        { role },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      const { access_token } = res.data;
      // Apri nuova tab — la pagina /impersonate-callback salva il token in
      // sessionStorage e ricarica la dashboard
      const url = `/impersonate-callback?token=${encodeURIComponent(access_token)}`;
      const win = window.open(url, "_blank", "noopener,noreferrer");
      if (!win) {
        toast.error("Il browser ha bloccato la nuova finestra. Abilita i popup per yollo.me");
      } else {
        toast.success(`Sessione impersonation aperta in nuova tab (${role})`);
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || "Errore impersonation");
    } finally {
      setImpersonating(null);
    }
  };

  return (
    <div className="space-y-5">
      {/* Back + Header */}
      <div className="flex items-center gap-4">
        <button onClick={onBack} className="w-9 h-9 rounded-xl bg-white border border-gray-200 flex items-center justify-center hover:bg-gray-50 transition-colors" data-testid="back-btn">
          <ArrowLeft className="h-4 w-4 text-gray-600" />
        </button>
        <div className="flex-1">
          <h1 className="text-xl font-bold text-gray-900">{societa.nome || societa.denominazione || societa.ragione_sociale}</h1>
          <p className="text-xs text-gray-500">ID: {societa.id?.substring(0, 8)}... &middot; {societa.users_count || 0} utenti &middot; {details?.locali_count || 0} locali</p>
        </div>
        <div className="flex items-center gap-2">
          {sub.gratuito && <span className="text-xs px-3 py-1.5 rounded-full bg-emerald-50 text-emerald-700 font-bold border border-emerald-200">GRATUITO</span>}
          <span className={`text-xs px-3 py-1.5 rounded-full font-bold border ${societa.attivo !== false ? "bg-emerald-50 text-emerald-700 border-emerald-200" : "bg-red-50 text-red-600 border-red-200"}`}>
            {societa.attivo !== false ? "ATTIVA" : "DISATTIVATA"}
          </span>
          <span className="text-xs px-3 py-1.5 rounded-full bg-indigo-50 text-indigo-700 font-bold border border-indigo-200 capitalize">{sub.durata || "annuale"}</span>
          {societa.attivo === false && (
            <button
              onClick={() => { setDeleteInput(""); setDeleteDialog(true); }}
              className="flex items-center gap-1.5 text-xs px-3 py-1.5 rounded-full font-bold border bg-red-600 text-white border-red-700 hover:bg-red-700 transition-colors"
              data-testid="btn-elimina-societa"
              title="Elimina società definitivamente"
            >
              <Trash2 className="h-3 w-3" /> Elimina
            </button>
          )}
        </div>
      </div>

      {/* ═══ AZIONI SUPPORTO — IMPERSONATION ═══ */}
      <div className="rounded-2xl bg-gradient-to-br from-violet-50 to-indigo-50 border border-violet-200 p-3 sm:p-4 flex items-center justify-between gap-3 flex-wrap">
        <div className="flex items-start gap-3 min-w-0">
          <div className="w-9 h-9 rounded-xl bg-violet-600 flex items-center justify-center flex-shrink-0">
            <Shield className="h-4 w-4 text-white" />
          </div>
          <div className="min-w-0">
            <p className="text-sm font-bold text-gray-900">Accesso Supporto SuperAdmin</p>
            <p className="text-xs text-gray-600 mt-0.5">Entra come utente per completare la configurazione o riprodurre un errore. Sessione 1h, audit log attivo.</p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <Button
            size="sm"
            onClick={() => handleImpersonate("admin")}
            disabled={!!impersonating || societa.attivo === false}
            className="rounded-xl bg-violet-600 hover:bg-violet-700 text-white text-xs font-bold px-4 disabled:opacity-50"
            data-testid="impersonate-admin-btn"
          >
            <UserCog className="h-3.5 w-3.5 mr-1.5" />
            {impersonating === "admin" ? "..." : "Entra come Admin"}
          </Button>
          <Button
            size="sm"
            onClick={() => handleImpersonate("operatore")}
            disabled={!!impersonating || societa.attivo === false}
            className="rounded-xl bg-blue-600 hover:bg-blue-700 text-white text-xs font-bold px-4 disabled:opacity-50"
            data-testid="impersonate-operatore-btn"
          >
            <HardHat className="h-3.5 w-3.5 mr-1.5" />
            {impersonating === "operatore" ? "..." : "Entra come Operatore"}
          </Button>
        </div>
      </div>

      {/* ═══ RICHIESTE PENDENTI ═══ */}
      {pendingReqs.length > 0 && (
        <Section title="Richieste Pendenti" icon={AlertTriangle} iconColor="bg-amber-500"
          badge={<span className="text-[10px] px-2 py-0.5 rounded-full bg-red-100 text-red-700 font-bold animate-pulse">{pendingReqs.length} in attesa</span>}>
          <div className="space-y-3">
            {pendingReqs.map(req => (
              <div key={req.id} className="flex items-center justify-between p-4 rounded-xl bg-amber-50 border border-amber-200" data-testid={`pending-req-${req.id}`}>
                <div>
                  <p className="text-sm font-bold text-gray-800">{req.tipo === "bot_telegram" ? "Bot Telegram" : req.tipo}</p>
                  <p className="text-xs text-gray-500">{req.tipo === "bot_telegram" && `${req.quantita} numer${req.quantita === 1 ? "o" : "i"} richiesti`} &middot; {req.created_at ? new Date(req.created_at).toLocaleDateString("it-IT") : ""}</p>
                </div>
                <div className="flex items-center gap-2">
                  <Button size="sm" onClick={() => resolveRequest(req.id, "approve")} className="rounded-xl bg-emerald-600 hover:bg-emerald-700 hover:text-white text-white text-xs font-bold px-4" data-testid={`approve-${req.id}`}>
                    <Check className="h-3 w-3 mr-1" /> Attiva
                  </Button>
                  <Button size="sm" variant="outline" onClick={() => resolveRequest(req.id, "reject")} className="rounded-xl border-slate-300 text-slate-700 hover:bg-slate-100 hover:text-slate-900 text-xs transition-colors" data-testid={`reject-${req.id}`}>
                    <X className="h-3 w-3 mr-1" /> Rifiuta
                  </Button>
                </div>
              </div>
            ))}
          </div>
        </Section>
      )}

      {/* ═══ RIEPILOGO ABBONAMENTO - REDESIGN UX ═══ */}
      <Section title="Abbonamento" icon={Package} iconColor="bg-indigo-500"
        actions={editingSection !== "abbonamento" ? (
          <div className="flex items-center gap-2">
            <Button
              size="sm" variant="outline"
              onClick={handleToggleWizard}
              disabled={activatingWizard}
              className="rounded-xl border-violet-300 text-violet-700 bg-white hover:bg-violet-50 text-xs font-semibold transition-colors"
              data-testid="toggle-wizard-btn"
              title="Attiva/disattiva il wizard di onboarding"
            >
              {activatingWizard ? "..." : <><span className="mr-1">⚡</span>{wizardActive ? "Disattiva Wizard" : "Attiva Wizard"}</>}
            </Button>
            <Button size="sm" variant="outline" onClick={() => startEdit("abbonamento", {
              package_ids: sub.package_ids || [], durata: sub.durata || "annuale", gratuito: sub.gratuito || false,
              trial_giorni: sub.trial_giorni || 0, trial_inizio: sub.trial_inizio || "", note: sub.note || "",
              codice_sconto: sub.codice_sconto || "",
              numeri_acquistati: numAcq, venditore_id: sub.venditore_id || "",
            })} className="rounded-xl border-slate-300 text-slate-700 bg-white hover:bg-slate-100 hover:text-slate-900 text-xs font-semibold transition-colors" data-testid="edit-abbonamento-btn">
              <Pencil className="h-3 w-3 mr-1" /> Modifica
            </Button>
          </div>
        ) : (
          <div className="flex items-center gap-2">
            <Button size="sm" variant="outline" onClick={cancelEdit} className="rounded-xl border-slate-300 text-slate-700 hover:bg-slate-100 hover:text-slate-900 text-xs transition-colors">Annulla</Button>
            <Button size="sm" onClick={saveEdit} className={`rounded-xl text-white text-xs font-bold ${hasDirtyFields ? "bg-red-500 hover:bg-red-600 hover:text-white" : "bg-slate-900 hover:bg-slate-700 hover:text-white"}`} data-testid="save-abbonamento-btn">
              <Save className="h-3 w-3 mr-1" /> Salva
            </Button>
          </div>
        )}>
        {editingSection !== "abbonamento" ? (
          /* ══════════ READ-ONLY MODE - NUOVO DESIGN ══════════ */
          <div className="space-y-5">
            {/* Account Gratuito Badge */}
            {sub.gratuito && (
              <div className="rounded-xl bg-gradient-to-br from-emerald-50 to-teal-50 border border-emerald-200 p-4 flex items-center gap-3" data-testid="free-badge">
                <div className="w-12 h-12 rounded-xl bg-emerald-100 flex items-center justify-center">
                  <Sparkles className="h-6 w-6 text-emerald-600" />
                </div>
                <div>
                  <p className="text-base font-bold text-emerald-700">Account Gratuito</p>
                  <p className="text-xs text-emerald-600">Accesso illimitato a tutte le funzionalita</p>
                </div>
              </div>
            )}

            {/* ── Cost Summary Card ── */}
            {!sub.gratuito && costs.lines.length > 0 && (
              <div className="rounded-xl bg-gradient-to-br from-indigo-50 to-blue-50 border border-indigo-200 p-4" data-testid="cost-summary">
                <div className="flex items-center justify-between mb-3">
                  <div className="flex items-center gap-2">
                    <Euro className="h-4 w-4 text-indigo-600" />
                    <span className="text-xs font-bold text-indigo-700 uppercase tracking-wider">Costo mensile</span>
                  </div>
                  <span className="text-2xl font-black text-indigo-700" data-testid="total-monthly-cost">{costs.total.toFixed(0)}€<span className="text-sm font-medium text-indigo-400">/mese</span></span>
                </div>
                <div className="space-y-1.5">
                  {costs.lines.map((line, i) => (
                    <div key={i} className="flex items-center justify-between text-sm">
                      <div className="flex items-center gap-2">
                        <span className="text-gray-700">{line.label}</span>
                        <span className="text-[9px] px-1.5 py-0.5 rounded-full bg-white/80 text-gray-500 font-bold">{line.tipo}</span>
                      </div>
                      <span className="font-semibold text-gray-800">{line.price.toFixed(0)}€</span>
                    </div>
                  ))}
                </div>
                <div className="mt-2 pt-2 border-t border-indigo-200/50 flex items-center justify-between">
                  <span className="text-[10px] text-indigo-500 capitalize">Piano {sub.durata || "annuale"}</span>
                  <span className="text-xs font-bold text-indigo-600">{(costs.total * 12).toFixed(0)}€ totale annuo</span>
                </div>
              </div>
            )}

            {/* Info Row per Trial */}
            {sub.trial_giorni > 0 && (
              <div className="flex items-center gap-3 p-3 rounded-xl bg-amber-50 border border-amber-200">
                <Clock className="h-4 w-4 text-amber-600" />
                <div>
                  <p className="text-xs font-bold text-amber-700">Trial Attivo</p>
                  <p className="text-[10px] text-amber-600">{sub.trial_giorni} giorni dal {sub.trial_inizio?.split("T")[0] || "N/D"}</p>
                </div>
              </div>
            )}

            {/* Venditore associato */}
            <div className="flex items-center justify-between p-3 rounded-xl bg-slate-50 border border-slate-100" data-testid="venditore-row">
              <div className="flex items-center gap-2">
                <Users className="h-4 w-4 text-slate-500" />
                <span className="text-xs font-bold text-slate-500 uppercase">Venditore</span>
              </div>
              <span className="text-sm font-semibold text-slate-700" data-testid="venditore-display">
                {sub.venditore_id ? (venditori.find(v => v.id === sub.venditore_id)?.nome || sub.venditore_id.slice(0, 8)) : <span className="text-slate-400 italic">Non assegnato</span>}
              </span>
            </div>

            {/* ══════════ PACCHETTI ATTIVI - RAGGRUPPATI ══════════ */}
            {(() => {
              const pkgBase = ownedPkgs.filter(p => p.tipo === "base");
              const pkgAddon = ownedPkgs.filter(p => p.tipo === "addon");
              const pkgServizio = ownedPkgs.filter(p => p.tipo === "servizio");
              const notOwned = allPackages.filter(p => !(sub.package_ids || []).includes(p.id));
              
              return (
                <div className="space-y-4">
                  {/* Pacchetti Base */}
                  {pkgBase.length > 0 && (
                    <div data-testid="pkg-section-base">
                      <div className="flex items-center gap-2 mb-2">
                        <div className="w-2 h-2 rounded-full bg-indigo-500" />
                        <span className="text-xs font-bold text-gray-600 uppercase tracking-wider">Pacchetti Base</span>
                        <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-indigo-100 text-indigo-700 font-bold">{pkgBase.length}</span>
                      </div>
                      <div className="space-y-2">
                        {pkgBase.map(pkg => (
                          <div key={pkg.id} className="flex items-center justify-between p-3 rounded-xl bg-indigo-50/50 border border-indigo-100" data-testid={`pkg-active-${pkg.id}`}>
                            <div className="flex items-center gap-3">
                              <div className="w-8 h-8 rounded-lg bg-indigo-100 flex items-center justify-center">
                                <Package className="h-4 w-4 text-indigo-600" />
                              </div>
                              <div>
                                <p className="text-sm font-semibold text-gray-800">{pkg.nome}</p>
                                {!sub.gratuito && <p className="text-[10px] text-gray-500">{(pkg.prezzo_mensile_annuale || 0).toFixed(0)}€/mese</p>}
                              </div>
                            </div>
                            <div className="flex items-center gap-2">
                              <span className="text-[10px] px-2 py-1 rounded-full bg-emerald-100 text-emerald-700 font-bold flex items-center gap-1">
                                <Check className="h-3 w-3" /> ATTIVO
                              </span>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Add-on */}
                  {pkgAddon.length > 0 && (
                    <div data-testid="pkg-section-addon">
                      <div className="flex items-center gap-2 mb-2">
                        <div className="w-2 h-2 rounded-full bg-amber-500" />
                        <span className="text-xs font-bold text-gray-600 uppercase tracking-wider">Add-on</span>
                        <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-amber-100 text-amber-700 font-bold">{pkgAddon.length}</span>
                      </div>
                      <div className="space-y-2">
                        {pkgAddon.map(pkg => (
                          <div key={pkg.id} className="flex items-center justify-between p-3 rounded-xl bg-amber-50/50 border border-amber-100" data-testid={`pkg-active-${pkg.id}`}>
                            <div className="flex items-center gap-3">
                              <div className="w-8 h-8 rounded-lg bg-amber-100 flex items-center justify-center">
                                <Plus className="h-4 w-4 text-amber-600" />
                              </div>
                              <div>
                                <p className="text-sm font-semibold text-gray-800">{pkg.nome}</p>
                                {!sub.gratuito && <p className="text-[10px] text-gray-500">{(pkg.prezzo_mensile_annuale || 0).toFixed(0)}€/mese</p>}
                              </div>
                            </div>
                            <span className="text-[10px] px-2 py-1 rounded-full bg-emerald-100 text-emerald-700 font-bold flex items-center gap-1">
                              <Check className="h-3 w-3" /> ATTIVO
                            </span>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Servizi */}
                  {pkgServizio.length > 0 && (
                    <div data-testid="pkg-section-servizio">
                      <div className="flex items-center gap-2 mb-2">
                        <div className="w-2 h-2 rounded-full bg-purple-500" />
                        <span className="text-xs font-bold text-gray-600 uppercase tracking-wider">Servizi</span>
                        <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-purple-100 text-purple-700 font-bold">{pkgServizio.length}</span>
                      </div>
                      <div className="space-y-2">
                        {pkgServizio.map(pkg => (
                          <div key={pkg.id} className="flex items-center justify-between p-3 rounded-xl bg-purple-50/50 border border-purple-100" data-testid={`pkg-active-${pkg.id}`}>
                            <div className="flex items-center gap-3">
                              <div className="w-8 h-8 rounded-lg bg-purple-100 flex items-center justify-center">
                                <Shield className="h-4 w-4 text-purple-600" />
                              </div>
                              <div>
                                <p className="text-sm font-semibold text-gray-800">{pkg.nome}</p>
                                {!sub.gratuito && <p className="text-[10px] text-gray-500">{(pkg.prezzo_mensile_annuale || 0).toFixed(0)}€/mese</p>}
                              </div>
                            </div>
                            <span className="text-[10px] px-2 py-1 rounded-full bg-emerald-100 text-emerald-700 font-bold flex items-center gap-1">
                              <Check className="h-3 w-3" /> ATTIVO
                            </span>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Nessun pacchetto */}
                  {ownedPkgs.length === 0 && (
                    <div className="text-center py-6 rounded-xl bg-gray-50 border border-gray-200" data-testid="no-packages">
                      <Package className="h-8 w-8 text-gray-300 mx-auto mb-2" />
                      <p className="text-sm font-medium text-gray-500">Nessun pacchetto attivo</p>
                      <p className="text-xs text-gray-400 mt-1">Clicca "Modifica" per associare i pacchetti</p>
                    </div>
                  )}

                  {/* Pacchetti Disponibili (non attivi) */}
                  {notOwned.length > 0 && ownedPkgs.length > 0 && (
                    <div className="pt-3 border-t border-gray-100">
                      <p className="text-[10px] font-bold text-gray-400 uppercase tracking-wider mb-2 flex items-center gap-1">
                        <Lock className="h-3 w-3" /> Pacchetti non attivi ({notOwned.length})
                      </p>
                      <div className="flex flex-wrap gap-1.5">
                        {notOwned.map(pkg => (
                          <span key={pkg.id} className="text-[10px] px-2 py-1 rounded-full bg-gray-100 text-gray-500 font-medium">{pkg.nome}</span>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              );
            })()}

            {/* Note */}
            {sub.note && (
              <div className="p-3 rounded-xl bg-gray-50 border border-gray-200">
                <p className="text-[10px] font-bold text-gray-500 uppercase tracking-wider mb-1">Note</p>
                <p className="text-sm text-gray-700">{sub.note}</p>
              </div>
            )}
          </div>
        ) : (
          /* ══════════ EDIT MODE - NUOVO DESIGN CON SWITCH ══════════ */
          <div className="space-y-5">
            {hasDirtyFields && (
              <div className="flex items-center gap-2 p-3 rounded-xl bg-red-50 border border-red-200 text-xs text-red-700 font-medium">
                <AlertTriangle className="h-3.5 w-3.5 flex-shrink-0" /> Hai modificato dei campi (evidenziati in rosso). Salvando altererai l'acquisto del cliente.
              </div>
            )}
            
            {/* Toggle Gratuito */}
            <div className={`flex items-center justify-between p-4 rounded-xl border transition-all ${isFieldDirty("gratuito") ? "border-red-400 ring-2 ring-red-400 bg-red-50/30" : "bg-gradient-to-r from-emerald-50 to-teal-50 border-emerald-200"}`}>
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-xl bg-emerald-100 flex items-center justify-center">
                  <Sparkles className="h-5 w-5 text-emerald-600" />
                </div>
                <div>
                  <p className="text-sm font-bold text-gray-800">Account Gratuito</p>
                  <p className="text-[10px] text-gray-500">Sblocca tutte le funzionalita senza costi</p>
                </div>
              </div>
              <Switch checked={editForm.gratuito} onCheckedChange={v => setEditForm(p => ({...p, gratuito: v}))} data-testid="switch-gratuito" />
            </div>
            
            {!editForm.gratuito && (
              <>
                {/* ══════════ SELEZIONE PACCHETTI CON SWITCH ══════════ */}
                <div className={`rounded-xl border p-4 transition-all ${isFieldDirty("package_ids") ? "ring-2 ring-red-400 border-red-400 bg-red-50/20" : "border-gray-200"}`}>
                  <div className="flex items-center justify-between mb-4">
                    <div className="flex items-center gap-2">
                      <Package className="h-4 w-4 text-indigo-600" />
                      <span className="text-xs font-bold text-gray-700 uppercase tracking-wider">Seleziona Pacchetti</span>
                    </div>
                    <span className="text-[10px] px-2 py-1 rounded-full bg-indigo-100 text-indigo-700 font-bold">
                      {editForm.package_ids?.length || 0} selezionati
                    </span>
                  </div>
                  
                  {/* Raggruppamento per tipo */}
                  {(() => {
                    const byTipo = { base: [], addon: [], servizio: [] };
                    allPackages.forEach(pkg => {
                      if (byTipo[pkg.tipo]) byTipo[pkg.tipo].push(pkg);
                      else byTipo.base.push(pkg);
                    });
                    
                    const tipoConfig = {
                      base: { label: "Pacchetti Base", color: "indigo", icon: Package },
                      addon: { label: "Add-on", color: "amber", icon: Plus },
                      servizio: { label: "Servizi", color: "purple", icon: Shield }
                    };
                    
                    return (
                      <div className="space-y-4">
                        {Object.entries(byTipo).filter(([, pkgs]) => pkgs.length > 0).map(([tipo, pkgs]) => {
                          const cfg = tipoConfig[tipo];
                          const TipoIcon = cfg.icon;
                          return (
                            <div key={tipo}>
                              <div className="flex items-center gap-2 mb-2">
                                <div className={`w-1.5 h-1.5 rounded-full bg-${cfg.color}-500`} />
                                <span className="text-[10px] font-bold text-gray-500 uppercase tracking-wider">{cfg.label}</span>
                              </div>
                              <div className="space-y-2">
                                {pkgs.map(pkg => {
                                  const isActive = editForm.package_ids?.includes(pkg.id);
                                  const price = pkg.prezzo_mensile_annuale || 0;
                                  return (
                                    <div 
                                      key={pkg.id} 
                                      className={`flex items-center justify-between p-3 rounded-xl border transition-all ${
                                        isActive 
                                          ? `bg-${cfg.color}-50 border-${cfg.color}-200` 
                                          : "bg-gray-50 border-gray-200 opacity-60"
                                      }`}
                                      data-testid={`pkg-toggle-${pkg.id}`}
                                    >
                                      <div className="flex items-center gap-3">
                                        <div className={`w-8 h-8 rounded-lg flex items-center justify-center ${isActive ? `bg-${cfg.color}-100` : "bg-gray-200"}`}>
                                          <TipoIcon className={`h-4 w-4 ${isActive ? `text-${cfg.color}-600` : "text-gray-400"}`} />
                                        </div>
                                        <div>
                                          <p className={`text-sm font-semibold ${isActive ? "text-gray-800" : "text-gray-500"}`}>{pkg.nome}</p>
                                          <p className="text-[10px] text-gray-500">{price.toFixed(0)}€/mese</p>
                                        </div>
                                      </div>
                                      <Switch
                                        checked={isActive}
                                        onCheckedChange={() => {
                                          setEditForm(p => ({
                                            ...p,
                                            package_ids: isActive
                                              ? p.package_ids.filter(id => id !== pkg.id)
                                              : [...p.package_ids, pkg.id]
                                          }));
                                        }}
                                        data-testid={`switch-pkg-${pkg.id}`}
                                      />
                                    </div>
                                  );
                                })}
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    );
                  })()}
                </div>

                {/* Durata e Trial */}
                <div className="grid grid-cols-2 gap-3">
                  <div className={`${isFieldDirty("durata") ? "ring-2 ring-red-400 rounded-xl bg-red-50/30" : ""}`}>
                    <Label className="text-xs font-bold text-gray-600 mb-1 block">Durata contratto</Label>
                    <div className="px-3 py-2.5 rounded-xl bg-indigo-50 border border-indigo-200 text-sm font-semibold text-indigo-700 flex items-center gap-2">
                      <Clock className="h-3.5 w-3.5" />
                      Annuale (12 mesi)
                    </div>
                  </div>
                  <div>
                    <Label className="text-xs font-bold text-gray-600 mb-1 block">Trial (giorni)</Label>
                    <Input 
                      type="number" 
                      value={editForm.trial_giorni} 
                      onChange={e => setEditForm(p => ({...p, trial_giorni: parseInt(e.target.value) || 0}))} 
                      className={`h-10 rounded-xl bg-gray-50 border-gray-200 text-gray-900 ${dirtyBorder("trial_giorni")}`} 
                      data-testid="input-trial-giorni"
                    />
                  </div>
                </div>

                {editForm.trial_giorni > 0 && (
                  <div>
                    <Label className="text-xs font-bold text-gray-600 mb-1 block">Data inizio trial</Label>
                    <DateInput value={editForm.trial_inizio?.split("T")[0] || ""} onChange={(v) => setEditForm(p => ({...p, trial_inizio: v}))} />
                  </div>
                )}
              </>
            )}
            
            {/* Venditore associato */}
            <div className={`${isFieldDirty("venditore_id") ? "ring-2 ring-red-400 rounded-xl bg-red-50/30 p-1" : ""}`}>
              <Label className="text-xs font-bold text-gray-600 mb-1 block">Venditore associato</Label>
              <select
                value={editForm.venditore_id || ""}
                onChange={e => setEditForm(p => ({...p, venditore_id: e.target.value}))}
                className="w-full h-10 rounded-xl bg-gray-50 border border-gray-200 text-gray-900 text-sm px-3"
                data-testid="select-venditore"
              >
                <option value="">— Nessun venditore —</option>
                {venditori.map(v => (
                  <option key={v.id} value={v.id}>{v.nome} ({v.codice_venditore})</option>
                ))}
              </select>
            </div>

            {/* Codice sconto applicato */}
            <div>
              <Label className="text-xs font-bold text-gray-600 mb-1 block">Codice sconto (applicato al prezzo e alle provvigioni)</Label>
              <Input
                value={editForm.codice_sconto || ""}
                onChange={e => setEditForm(p => ({...p, codice_sconto: e.target.value.toUpperCase().trim()}))}
                className={`h-10 rounded-xl bg-gray-50 border-gray-200 text-gray-900 uppercase ${dirtyBorder("codice_sconto")}`}
                placeholder="Es. BENVENUTO2026 (vuoto = nessuno)"
                data-testid="input-codice-sconto"
              />
            </div>

            {/* Note interne */}
            <div>
              <Label className="text-xs font-bold text-gray-600 mb-1 block">Note interne</Label>
              <Input 
                value={editForm.note || ""} 
                onChange={e => setEditForm(p => ({...p, note: e.target.value}))} 
                className={`h-10 rounded-xl bg-gray-50 border-gray-200 text-gray-900 ${dirtyBorder("note")}`} 
                placeholder="Note visibili solo al superadmin..."
                data-testid="input-note"
              />
            </div>
          </div>
        )}
      </Section>

      {/* ═══ BOT TELEGRAM ═══ */}
      <Section title="Bot Telegram" icon={Bot} iconColor="bg-blue-500"
        badge={numAcq > 0 ? <span className="text-[10px] px-2 py-0.5 rounded-full bg-blue-50 text-blue-700 font-bold border border-blue-200">{numAcq} numeri &middot; {destinatari.length} destinatari</span> : null}
        actions={editingSection !== "bot" ? (
          <Button size="sm" variant="outline" onClick={() => startEdit("bot", {
            numeri_acquistati: numAcq, destinatari: [...destinatari],
          })} className="rounded-xl border-slate-300 text-slate-700 bg-white hover:bg-slate-100 hover:text-slate-900 text-xs font-semibold transition-colors" data-testid="edit-bot-btn">
            <Pencil className="h-3 w-3 mr-1" /> Modifica
          </Button>
        ) : (
          <div className="flex items-center gap-2">
            <Button size="sm" variant="outline" onClick={cancelEdit} className="rounded-xl border-slate-300 text-slate-700 hover:bg-slate-100 hover:text-slate-900 text-xs transition-colors">Annulla</Button>
            <Button size="sm" onClick={saveEdit} className={`rounded-xl text-white text-xs font-bold ${hasDirtyFields ? "bg-red-500 hover:bg-red-600 hover:text-white" : "bg-slate-900 hover:bg-slate-700 hover:text-white"}`} data-testid="save-bot-btn">
              <Save className="h-3 w-3 mr-1" /> Salva
            </Button>
          </div>
        )}>
        {editingSection !== "bot" ? (
          /* READ-ONLY */
          <div className="space-y-3">
            <div className="rounded-xl bg-blue-50/60 border border-blue-100 p-3 flex items-start gap-2.5">
              <Bot className="h-4 w-4 text-blue-500 mt-0.5 flex-shrink-0" />
              <p className="text-xs text-blue-900 leading-relaxed">
                Bot centralizzato <b>@yollo_ai_bot</b>. Token e webhook sono gestiti globalmente da <i>Impostazioni → Bot Telegram</i>.
              </p>
            </div>
            <InfoRow label="Numeri acquistati" value={numAcq} />
            <InfoRow label="Slot configurati" value={`${destinatari.length}/${numAcq}`} />
            {destinatari.length > 0 && (
              <div className="pt-2">
                <p className="text-xs font-bold text-gray-500 uppercase tracking-wider mb-2">Destinatari ({destinatari.length}/{numAcq})</p>
                {destinatari.map((d, i) => (
                  <div key={i} className="flex items-center gap-2 py-1.5">
                    {d.telegram_id ? <CheckCircle2 className="h-3.5 w-3.5 text-emerald-500" /> : <Link2 className="h-3.5 w-3.5 text-amber-500" />}
                    <span className="text-sm text-gray-800">{d.nome || `Slot ${i + 1}`}</span>
                    <span className="text-xs text-gray-400 font-mono">{d.telegram_id || "(in attesa)"}</span>
                  </div>
                ))}
              </div>
            )}
          </div>
        ) : (
          /* EDIT MODE */
          <div className="space-y-4">
            {hasDirtyFields && (
              <div className="flex items-center gap-2 p-3 rounded-xl bg-red-50 border border-red-200 text-xs text-red-700 font-medium">
                <AlertTriangle className="h-3.5 w-3.5" /> Stai modificando la configurazione bot del cliente.
              </div>
            )}
            <div className="rounded-xl bg-blue-50/60 border border-blue-100 p-3 flex items-start gap-2.5">
              <Bot className="h-4 w-4 text-blue-500 mt-0.5 flex-shrink-0" />
              <p className="text-xs text-blue-900 leading-relaxed">
                Bot centralizzato <b>@yollo_ai_bot</b>. Da qui puoi solo gestire i numeri acquistati e i destinatari. Token e webhook globali in <i>Impostazioni → Bot Telegram</i>.
              </p>
            </div>
            <div className={`flex items-center justify-between p-3 rounded-xl border transition-all ${isFieldDirty("numeri_acquistati") ? "border-red-400 ring-2 ring-red-400 bg-red-50/30" : "bg-gray-50 border-gray-200"}`}>
              <div><p className="text-sm font-bold text-gray-800">Numeri acquistati</p><p className="text-xs text-gray-500">0 = disattiva servizio</p></div>
              <div className="flex items-center gap-2">
                <button type="button" onClick={() => setEditForm(p => ({...p, numeri_acquistati: Math.max(0, p.numeri_acquistati - 1)}))} className="w-8 h-8 rounded-lg bg-white border border-gray-300 flex items-center justify-center text-gray-700 hover:bg-gray-100 font-bold">-</button>
                <span className="w-8 text-center font-bold text-gray-900">{editForm.numeri_acquistati}</span>
                <button type="button" onClick={() => setEditForm(p => ({...p, numeri_acquistati: p.numeri_acquistati + 1}))} className="w-8 h-8 rounded-lg bg-blue-600 text-white flex items-center justify-center font-bold hover:bg-blue-700">+</button>
              </div>
            </div>
            {editForm.destinatari?.length > 0 && (
              <div className={`rounded-xl p-3 space-y-2 ${isFieldDirty("destinatari") ? "ring-2 ring-red-400 bg-red-50/30" : "bg-gray-50"}`}>
                <p className="text-xs font-bold text-gray-600 uppercase tracking-wider">Destinatari</p>
                {editForm.destinatari.map((dest, i) => (
                  <div key={i} className="flex items-center justify-between py-2 px-3 rounded-lg bg-white border border-gray-200">
                    <div className="flex items-center gap-2">
                      {dest.telegram_id ? <CheckCircle2 className="h-3 w-3 text-emerald-500" /> : <Link2 className="h-3 w-3 text-amber-500" />}
                      <span className="text-xs font-medium text-gray-800">{dest.nome || `Slot ${i + 1}`}</span>
                      <span className="text-[10px] text-gray-400 font-mono">{dest.telegram_id || "(in attesa)"}</span>
                    </div>
                    <button type="button" onClick={() => setEditForm(p => ({...p, destinatari: p.destinatari.filter((_, j) => j !== i)}))} className="w-6 h-6 rounded-md bg-red-50 hover:bg-red-100 flex items-center justify-center"><X className="h-3 w-3 text-red-500" /></button>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
      </Section>

      {/* ═══ LOCALI ═══ */}
      <Section title="Locali" icon={MapPin} iconColor="bg-orange-500"
        badge={<span className="text-[10px] px-2 py-0.5 rounded-full bg-orange-50 text-orange-700 font-bold border border-orange-200">{details?.locali_count || 0} registrati</span>}
        actions={editingSection !== "locali" ? (
          <Button size="sm" variant="outline" onClick={() => startEdit("locali", {
            locali_disattivati: (details?.locali || []).filter(l => l.attivo === false).map(l => l.id),
          })} className="rounded-xl border-slate-300 text-slate-700 bg-white hover:bg-slate-100 hover:text-slate-900 text-xs font-semibold transition-colors" data-testid="edit-locali-btn">
            <Pencil className="h-3 w-3 mr-1" /> Gestisci
          </Button>
        ) : (
          <div className="flex items-center gap-2">
            <Button size="sm" variant="outline" onClick={cancelEdit} className="rounded-xl border-slate-300 text-slate-700 hover:bg-slate-100 hover:text-slate-900 text-xs transition-colors">Annulla</Button>
            <Button size="sm" onClick={saveEdit} className={`rounded-xl text-white text-xs font-bold ${hasDirtyFields ? "bg-red-500 hover:bg-red-600 hover:text-white" : "bg-slate-900 hover:bg-slate-700 hover:text-white"}`} data-testid="save-locali-btn"><Save className="h-3 w-3 mr-1" /> Salva</Button>
          </div>
        )}>
        <div className="space-y-2">
          {(details?.locali || []).map((l, i) => {
            const isDisabled = editingSection === "locali" ? (editForm.locali_disattivati || []).includes(l.id) : l.attivo === false;
            return (
              <div key={l.id || i} className={`flex items-center justify-between p-3 rounded-xl border transition-all ${isDisabled ? "bg-red-50/40 border-red-200" : "bg-gray-50 border-gray-200"}`}>
                <div className="flex items-center gap-2">
                  <MapPin className={`h-3.5 w-3.5 ${isDisabled ? "text-red-300" : "text-orange-400"}`} />
                  <span className={`text-sm font-medium ${isDisabled ? "text-gray-400 line-through" : "text-gray-800"}`}>{l.nome}</span>
                  {i === 0 && <span className="text-[10px] bg-emerald-50 text-emerald-700 px-1.5 py-0.5 rounded-full font-bold">Incluso</span>}
                </div>
                {editingSection === "locali" && (
                  <button type="button" onClick={() => setEditForm(p => ({ ...p, locali_disattivati: isDisabled ? p.locali_disattivati.filter(id => id !== l.id) : [...p.locali_disattivati, l.id] }))}
                    className={`px-3 py-1 rounded-lg text-xs font-bold ${isDisabled ? "bg-emerald-100 text-emerald-700 hover:bg-emerald-200" : "bg-red-100 text-red-600 hover:bg-red-200"}`}>
                    {isDisabled ? "Riattiva" : "Disabilita"}
                  </button>
                )}
                {editingSection !== "locali" && (
                  <span className={`text-[10px] px-2 py-0.5 rounded-full font-bold ${isDisabled ? "bg-red-100 text-red-600" : "bg-emerald-50 text-emerald-700"}`}>
                    {isDisabled ? "DISABILITATO" : "ATTIVO"}
                  </span>
                )}
              </div>
            );
          })}
          {(details?.locali || []).length === 0 && <p className="text-xs text-gray-400 text-center py-3">Nessun locale</p>}
        </div>
      </Section>

      {/* ═══ OFFERTE & PROMOZIONI ═══ */}
      <Section title="Offerte & Promozioni" icon={Sparkles} iconColor="bg-amber-500"
        actions={<Button size="sm" variant="outline" onClick={() => { setOfferItems({ packages: {}, telegram_numeri: 0, locale_extra: 0 }); setOfferPrezzo(0); setOfferNote(""); setOfferDialog(true); }}
          className="rounded-xl border-slate-300 text-slate-700 bg-white hover:bg-slate-100 hover:text-slate-900 text-xs font-semibold transition-colors" data-testid="new-offer-btn"><Plus className="h-3 w-3 mr-1" /> Nuova Offerta</Button>}>
        {promoOffers.length > 0 ? (
          <div className="space-y-2">
            {promoOffers.map(o => (
              <div key={o.id} className={`flex items-center justify-between p-3 rounded-xl border ${o.stato === "pending" ? "bg-amber-50 border-amber-200" : o.stato === "accepted" ? "bg-emerald-50 border-emerald-200" : "bg-gray-50 border-gray-200"}`}>
                <div>
                  <p className="text-sm font-medium text-gray-800">{o.summary_label || o.package_nome}</p>
                  <p className="text-xs text-gray-500">{o.is_regalo ? "Regalo" : `${o.prezzo_offerta}€/mese (da ${o.prezzo_originale}€)`} &middot; {o.created_at ? new Date(o.created_at).toLocaleDateString("it-IT") : ""}</p>
                </div>
                <span className={`text-[10px] px-2 py-0.5 rounded-full font-bold ${o.stato === "pending" ? "bg-amber-100 text-amber-700" : o.stato === "accepted" ? "bg-emerald-100 text-emerald-700" : "bg-gray-100 text-gray-600"}`}>
                  {o.stato === "pending" ? "IN ATTESA" : o.stato === "accepted" ? "ACCETTATA" : o.stato?.toUpperCase()}
                </span>
              </div>
            ))}
          </div>
        ) : <p className="text-xs text-gray-400 text-center py-3">Nessuna offerta inviata</p>}
      </Section>

      {/* ═══ STORICO PAGAMENTI ═══ */}
      <Section title="Storico Pagamenti" icon={CreditCard} iconColor="bg-slate-600"
        badge={transactions.length > 0 ? <span className="text-[10px] px-2 py-0.5 rounded-full bg-gray-100 text-gray-600 font-bold border border-gray-200">{transactions.length} transazioni</span> : null}>
        {transactions.length > 0 ? (
          <div className="space-y-2">
            {transactions.map((tx, i) => (
              <div key={i} className="flex items-center justify-between p-3 rounded-xl bg-gray-50 border border-gray-200">
                <div className="flex items-center gap-3">
                  <div className={`w-8 h-8 rounded-lg flex items-center justify-center ${tx.payment_status === "paid" ? "bg-emerald-100" : tx.payment_status === "pending" ? "bg-amber-100" : "bg-red-100"}`}>
                    <Receipt className={`h-4 w-4 ${tx.payment_status === "paid" ? "text-emerald-600" : tx.payment_status === "pending" ? "text-amber-600" : "text-red-500"}`} />
                  </div>
                  <div>
                    <p className="text-sm font-medium text-gray-800">
                      {tx.tipo === "rinnovo" ? "Rinnovo abbonamento" : tx.tipo === "acquisto" ? "Nuovo acquisto" : tx.tipo === "addon" ? "Acquisto add-on" : tx.tipo || "Transazione"}
                    </p>
                    <p className="text-[10px] text-gray-500">
                      {tx.created_at ? new Date(tx.created_at).toLocaleDateString("it-IT", { day: "2-digit", month: "long", year: "numeric" }) : ""}
                      {tx.payment_method && <> &middot; {tx.payment_method}</>}
                    </p>
                  </div>
                </div>
                <div className="text-right">
                  <span className={`text-sm font-bold ${tx.payment_status === "paid" ? "text-emerald-700" : "text-gray-500"}`}>
                    {tx.amount ? `${tx.amount.toFixed(2)}€` : "\u2014"}
                  </span>
                  <p className={`text-[10px] font-semibold ${tx.payment_status === "paid" ? "text-emerald-600" : tx.payment_status === "pending" ? "text-amber-600" : "text-red-500"}`}>
                    {tx.payment_status === "paid" ? "Pagato" : tx.payment_status === "pending" ? "In attesa" : tx.payment_status || ""}
                  </p>
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className="text-center py-6">
            <CreditCard className="h-8 w-8 text-gray-200 mx-auto mb-2" />
            <p className="text-xs text-gray-400">Nessun pagamento registrato</p>
            <p className="text-[10px] text-gray-300 mt-1">Lo storico fatture sara disponibile prossimamente</p>
          </div>
        )}

        {/* Fatture - Coming soon */}
        <div className="mt-4 pt-4 border-t border-gray-100">
          <div className="flex items-center gap-2 text-gray-400">
            <FileText className="h-4 w-4" />
            <span className="text-xs font-medium">Storico Fatture</span>
            <span className="text-[9px] px-1.5 py-0.5 rounded-full bg-gray-100 text-gray-400 font-bold">PROSSIMAMENTE</span>
          </div>
        </div>
      </Section>

      {/* ═══ CONFIRM DIALOG ═══ */}
      <Dialog open={confirmDialog} onOpenChange={setConfirmDialog}>
        <DialogContent className="bg-white border-gray-200 text-gray-900 max-w-md rounded-2xl p-0 overflow-hidden" data-testid="confirm-save-dialog">
          <div className="bg-gradient-to-br from-red-500 to-rose-600 p-6 text-center">
            <div className="w-14 h-14 rounded-2xl bg-white/20 backdrop-blur-sm flex items-center justify-center mx-auto mb-3">
              <AlertTriangle className="h-7 w-7 text-white" />
            </div>
            <h2 className="text-lg font-black text-white mb-1">Attenzione!</h2>
            <p className="text-sm text-white/80">Stai modificando i dati del cliente</p>
          </div>
          <div className="p-5 space-y-4">
            <p className="text-sm text-gray-600">Questa azione <span className="font-bold text-red-600">potrebbe compromettere</span> gli acquisti e le impostazioni del cliente.</p>
            <div className="p-3 rounded-xl bg-red-50 border border-red-100 space-y-1.5">
              <p className="text-xs font-bold text-red-700 uppercase tracking-wider">Campi modificati:</p>
              {dirtyFields.map(k => (
                <div key={k} className="flex items-center gap-2 text-xs text-red-600"><div className="w-1.5 h-1.5 rounded-full bg-red-400" /><span className="font-medium">{labelMap[k] || k}</span></div>
              ))}
            </div>
            <div className="grid grid-cols-2 gap-3 pt-1">
              <Button variant="outline" onClick={() => setConfirmDialog(false)} className="rounded-xl border-slate-300 text-slate-700 hover:bg-slate-100 hover:text-slate-900 font-semibold transition-colors">Annulla</Button>
              <Button onClick={saveEdit} className="rounded-xl bg-red-500 hover:bg-red-600 hover:text-white text-white font-semibold transition-colors"><Save className="h-4 w-4 mr-1" /> Conferma</Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* ═══ OFFER DIALOG ═══ */}
      <Dialog open={offerDialog} onOpenChange={setOfferDialog}>
        <DialogContent className="bg-white border-gray-200 text-gray-900 max-w-lg max-h-[85vh] overflow-y-auto rounded-2xl" data-testid="offer-dialog">
          <DialogHeader><DialogTitle className="text-gray-900">Crea Offerta</DialogTitle></DialogHeader>
          <div className="space-y-5">
            <div className="p-3 rounded-xl bg-indigo-50 border border-indigo-200 text-xs text-indigo-800">Abbonamento <span className="font-bold">{sub.durata || "annuale"}</span> di <span className="font-bold">{societa.nome || societa.denominazione}</span></div>
            {/* Packages */}
            <div>
              <Label className="text-xs font-bold text-gray-700 uppercase tracking-wider">Pacchetti</Label>
              <div className="space-y-1.5 mt-2">
                {allPackages.filter(pkg => !(sub.package_ids || []).includes(pkg.id)).map(pkg => {
                  const isSel = !!offerItems.packages[pkg.id];
                  const pr = pkg.prezzo_mensile_annuale || 0;
                  return (
                    <button key={pkg.id} type="button" onClick={() => setOfferItems(p => ({...p, packages: {...p.packages, [pkg.id]: !p.packages[pkg.id]}}))}
                      className={`w-full text-left px-3 py-2.5 rounded-xl border transition-all ${isSel ? "bg-indigo-50 border-indigo-300 ring-1 ring-indigo-200" : "bg-gray-50 border-gray-200 hover:border-gray-300"}`}>
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-2">{isSel ? <Check className="h-4 w-4 text-indigo-600" /> : <div className="w-4 h-4 rounded border border-gray-300" />}<span className="text-sm font-medium text-gray-800">{pkg.nome}</span></div>
                        <span className="text-xs font-bold text-gray-600">{pr.toFixed(0)}€/mese</span>
                      </div>
                    </button>
                  );
                })}
              </div>
            </div>
            {/* Telegram */}
            <div className="flex items-center gap-3 p-3 rounded-xl bg-blue-50 border border-blue-200">
              <Bot className="h-5 w-5 text-blue-600" /><div className="flex-1"><Label className="text-xs font-bold text-gray-700">Bot Telegram (numeri)</Label></div>
              <div className="flex items-center gap-2">
                <button type="button" onClick={() => setOfferItems(p => ({...p, telegram_numeri: Math.max(0, p.telegram_numeri - 1)}))} className="w-7 h-7 rounded-lg bg-white border border-gray-300 flex items-center justify-center text-gray-700 hover:bg-gray-100 font-bold">-</button>
                <span className="w-8 text-center font-bold text-gray-900">{offerItems.telegram_numeri}</span>
                <button type="button" onClick={() => setOfferItems(p => ({...p, telegram_numeri: p.telegram_numeri + 1}))} className="w-7 h-7 rounded-lg bg-blue-600 text-white flex items-center justify-center font-bold hover:bg-blue-700">+</button>
              </div>
            </div>
            {/* Locali */}
            <div className="flex items-center gap-3 p-3 rounded-xl bg-orange-50 border border-orange-200">
              <MapPin className="h-5 w-5 text-orange-600" /><div className="flex-1"><Label className="text-xs font-bold text-gray-700">Locale aggiuntivo</Label></div>
              <div className="flex items-center gap-2">
                <button type="button" onClick={() => setOfferItems(p => ({...p, locale_extra: Math.max(0, p.locale_extra - 1)}))} className="w-7 h-7 rounded-lg bg-white border border-gray-300 flex items-center justify-center text-gray-700 hover:bg-gray-100 font-bold">-</button>
                <span className="w-8 text-center font-bold text-gray-900">{offerItems.locale_extra}</span>
                <button type="button" onClick={() => setOfferItems(p => ({...p, locale_extra: p.locale_extra + 1}))} className="w-7 h-7 rounded-lg bg-orange-600 text-white flex items-center justify-center font-bold hover:bg-orange-700">+</button>
              </div>
            </div>
            {/* Price */}
            {hasOfferItems && (
              <div className="border-t border-gray-100 pt-4 space-y-3">
                <div className="p-3 rounded-xl bg-gray-50 border border-gray-200">
                  <div className="flex items-center justify-between"><span className="text-xs text-gray-500">Prezzo listino</span><span className="text-lg font-black text-gray-900">{offerOrigTotal.toFixed(0)}€/mese</span></div>
                </div>
                <div><Label className="text-xs font-semibold text-gray-700">Prezzo offerta (€/mese) — 0 = regalo</Label><Input type="number" min={0} value={offerPrezzo} onChange={e => setOfferPrezzo(parseFloat(e.target.value) || 0)} className="h-12 rounded-xl bg-gray-50 border-gray-200 text-gray-900 text-xl font-black mt-1" data-testid="offer-price-input" /></div>
                {offerPrezzo === 0 && <p className="text-xs text-emerald-700 font-bold">Verra regalato al cliente!</p>}
                {offerPrezzo > 0 && offerPrezzo < offerOrigTotal && <p className="text-xs text-amber-700 font-bold">Sconto del {Math.round((1 - offerPrezzo / offerOrigTotal) * 100)}%</p>}
                <div><Label className="text-xs font-semibold text-gray-700">Note</Label><Input value={offerNote} onChange={e => setOfferNote(e.target.value)} className="h-10 rounded-xl bg-gray-50 border-gray-200 text-gray-900 mt-1" placeholder="Note..." /></div>
              </div>
            )}
          </div>
          <DialogFooter>
            <Button onClick={sendOffer} disabled={!hasOfferItems} className={`w-full rounded-xl text-white font-semibold ${!hasOfferItems ? "bg-gray-300" : offerPrezzo === 0 ? "bg-emerald-600 hover:bg-emerald-700" : "bg-indigo-600 hover:bg-indigo-700"}`} data-testid="send-offer-btn">
              {offerPrezzo === 0 ? <><Sparkles className="h-4 w-4 mr-1" /> Regala</> : <><Tag className="h-4 w-4 mr-1" /> Invia Offerta</>}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* ═══ DIALOG ELIMINA SOCIETÀ ═══ */}
      <Dialog open={deleteDialog} onOpenChange={setDeleteDialog}>
        <DialogContent className="max-w-md rounded-2xl">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-red-600">
              <Trash2 className="h-5 w-5" />
              Elimina società definitivamente
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div className="bg-red-50 border border-red-200 rounded-xl p-4 space-y-1">
              <p className="text-sm font-bold text-red-700">Attenzione — operazione irreversibile</p>
              <p className="text-xs text-red-600">
                Tutti i dati di <strong>{societa.nome || societa.ragione_sociale}</strong> verranno eliminati definitivamente:
                utenti, locali, turni, acquisti, ricette, fatture e ogni altro dato associato.
              </p>
              <p className="text-xs text-red-600 mt-1">
                I backup esistenti rimangono disponibili per i prossimi 30 giorni, poi spariranno anch'essi.
              </p>
            </div>
            <div className="space-y-1.5">
              <Label className="text-sm font-semibold text-gray-700">
                Scrivi <span className="font-black text-red-600">"elimina"</span> per confermare
              </Label>
              <Input
                value={deleteInput}
                onChange={e => setDeleteInput(e.target.value)}
                placeholder="elimina"
                className="rounded-xl border-gray-200 focus:border-red-400 focus:ring-red-200"
                data-testid="input-conferma-elimina"
                autoComplete="off"
              />
            </div>
          </div>
          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={() => setDeleteDialog(false)} className="rounded-xl" data-testid="btn-annulla-elimina">
              Annulla
            </Button>
            <Button
              onClick={deleteSocieta}
              disabled={deleteInput.trim().toLowerCase() !== "elimina" || deleting}
              className="rounded-xl bg-red-600 hover:bg-red-700 text-white font-bold disabled:opacity-40"
              data-testid="btn-conferma-elimina"
            >
              {deleting ? <><RefreshCw className="h-4 w-4 mr-1.5 animate-spin" />Eliminazione...</> : <><Trash2 className="h-4 w-4 mr-1.5" />Elimina definitivamente</>}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default SuperAdminSocietaDetail;
